<template>
  <div class="jwl-announcement" v-if="isAuthorised(rolesAndRightsList.RIGHT__ANNOUNCEMENT__DISPLAY, false)">
    <div class="jwl-announcement__box" v-if="!loaded && !error" v-t="'home.announcements.loading'" />

    <div class="jwl-announcement__box" v-else-if="!loaded && error">
      <JwlError :jwl-error="error" />
    </div>

    <div class="jwl-announcement__box" :class="boxClass" v-else>
      <div class="jwl-announcement__content" v-if="announcement">
        <div class="jwl-announcement__text-wrapper">
          <h3 class="jwl-announcement__headline">
            {{announcement.subject}}
          </h3>

          <JwlShowMore :disabled="route.name === 'announcement'" :jwl-text="announcement.text" />

          <div class="jwl-announcement__visible-attachment" v-for="(attachment, index) in getVisibleAttachments(announcement)" :key="`announcement-image-${index}`">
            <img v-if="attachment.type === 'image'" :src="attachment.url">
            <JwlVideoPlayer v-if="attachment.type === 'video'" :video-file="attachment"></JwlVideoPlayer>
          </div>
        </div>

        <CourseDocument
          v-if="announcement.attachments.length > 0"
          :jwl-file="announcement.attachments" />

        <div class="jwl-announcement__meta">
          <p v-if="announcement?.isGlobal || announcement?.courseCode === null">
            {{t('home.announcements.metaGlobal', {publishedBy: announcement.createdBy, publishedAt: localizedDate(announcement.createdAt)})}}
          </p>
          <p v-else>
            {{t('home.announcements.meta', {publishedBy: announcement.createdBy, publishedAt: localizedDate(announcement.createdAt), publishedFor: announcement.courseCode})}}
          </p>

          <p v-if="isAuthorised(rolesAndRightsList.RIGHT__ANNOUNCEMENT__EDIT, false) && announcement?.expiresAt">
            {{t('home.announcements.expiresAt', { expireDate: localizedDate(announcement.expiresAt) })}}
          </p>

          <JwlButton
            v-if="isAuthorised(rolesAndRightsList.RIGHT__ANNOUNCEMENT__EDIT, false) && withEdit"
            button-small
            @button-clicked="$emit('edit-announcement', announcement)">
            {{t('home.announcements.edit')}}
          </JwlButton>
        </div>
      </div>

      <div class="jwl-announcement__content" v-else v-t="'home.announcements.noAnnouncements'" />
    </div>

    <div class="jwl-announcement__dot-container" aria-hidden="true" v-if="loaded && listAnnouncements.length > 1">
      <ul class="jwl-announcement__dots" :class="dotsClass">
        <li class="jwl-announcement__dot"
          v-for="(a, index) in listAnnouncements"
          :key="`announcement-${index}`"
          :class="dotActiveClass(index, a.programmeType)">
          <div class="jwl-announcement__bar"
            :class="barActiveClass(index)"
            @click="changeAnnouncement(index)" />

          <div class="jwl-announcement__preview">
            <div class="jwl-announcement__preview-content" @click="changeAnnouncement(index)">
              <div class="jwl-announcement__meta-headline">
                <span>
                  {{a.subject}}
                </span>

                <JwlToggle class="jwl-announcement__mobile-toggle" :is-open="mobileVisibleAnnouncements.includes(index)" />
              </div>

              <p class="jwl-announcement__meta" v-if="a.isGlobal || a.courseCode === null">
                {{t('home.announcements.metaGlobal', {publishedBy: a.createdBy, publishedAt: a.createdDate})}}
              </p>
              <p class="jwl-announcement__meta" v-else>
                {{t('home.announcements.meta', {publishedBy: a.createdBy, publishedAt: a.createdDate, publishedFor: a.courseCode})}}
              </p>
            </div>

            <div class="jwl-announcement__preview-text" v-html="a.text" v-if="mobileVisibleAnnouncements.includes(index)" />
          </div>
        </li>
        <li
          class="jwl-announcement__show-all-button"
          v-if="listAnnouncements.length > 3 && !mobileShowMore"
          @click="showAllAnnouncements"
          v-html="t('general.showMore')" />
      </ul>
    </div>
  </div>
</template>

<script setup>
  import { localizedDate } from '@base/helper/localizedDate';

  const props = defineProps({
    courseAnnouncements: Array,
    withEdit: Boolean,
  });

  const { t } = useI18n();
  const route = useRoute();
  const { isAuthorised, rolesAndRightsList } = useUserAuthorisation();

  const learningStore = useModuleStore();

  const activeIndex = ref(0);
  const doShowMore = ref(false);
  const textWrapperStyle = ref('');
  const announcements = ref([]);
  const mobileVisibleAnnouncements = ref([]);
  const loaded = ref(false);
  const error = ref(null);
  const mobileShowMore = ref(false);

  const changeAnnouncement = (targetIndex) => {
    if (listAnnouncements.value.length > targetIndex) {
      activeIndex.value = targetIndex;
    } else {
      activeIndex.value = 0;
    }
    doShowMore.value = false;
    textWrapperStyle.value = '';

    if (mobileVisibleAnnouncements.value.includes(targetIndex)) {
      const mobileIndex = mobileVisibleAnnouncements.value.indexOf(targetIndex);
      mobileVisibleAnnouncements.value.splice(mobileIndex, 1);
    } else {
      mobileVisibleAnnouncements.value.push(targetIndex);
    }
  };

  const barActiveClass = (index) => ({
    'jwl-announcement__bar--active': activeIndex.value === index,
  });

  const dotActiveClass = (index, programmeType) => ({
    'jwl-announcement__dot--bachelor': programmeType === 'bachelor',
    'jwl-announcement__dot--professional': programmeType === 'professional',
    'jwl-announcement__dot--mobile-active': mobileVisibleAnnouncements.value.includes(index),
  });

  const showAllAnnouncements = () => {
    mobileShowMore.value = true;
  };

  const announcement = computed(() => listAnnouncements.value.length > activeIndex.value
      ? listAnnouncements.value[activeIndex.value]
      : null);

  const listAnnouncements = computed(() => {
    if (props.courseAnnouncements) {
      announcements.value = props.courseAnnouncements;
    }

    return announcements.value.slice(0, 50);
  });

  const boxClass = computed(() => ({
    'jwl-announcement__box--bachelor': announcement.value?.programmeType === 'bachelor',
    'jwl-announcement__box--professional': announcement.value?.programmeType === 'professional',
  }));

  const textWrapperClass = computed(() => ({
    'jwl-announcement__text-wrapper--show-overflow': doShowMore.value,
  }));

  const dotsClass = computed(() => ({
    'jwl-announcement__dots--show-all-announcements': mobileShowMore,
  }));

  const getVisibleAttachments = (currentAnnouncement) => {
    const visibleAttachments = [];
    currentAnnouncement.attachments.forEach((attachment) => {
      if ((attachment.type === 'image' || attachment.type === 'video') && 'url' in attachment) {
        visibleAttachments.push(attachment);
      }
    });
    return visibleAttachments;
  };

  if (props.courseAnnouncements === undefined) {
    learningStore.getData('announcement')
      .then((data) => {
        announcements.value = data;
        announcements.value.sort((a, b) => Date.parse(b.createdAt ?? '') - Date.parse(a.createdAt ?? ''));
        loaded.value = true;
      })
      .catch((e) => {
        error.value = e;
      });
  } else {
    loaded.value = true;
  }
</script>

<style lang="scss">
@use "@base/styles/mixins";
@use "@base/styles/variables";

.jwl-announcement {
  $root: &;

  display: flex;
  flex-flow: column;

  a {
    color: currentColor;
    transition: color .25s;

    &:hover {
      color: var(--concentration-color, var(--color-blue));
    }
  }

  img {
    max-width: 100%;
  }

  &__content {
    display: flex;
    flex-flow: column;
  }

  &__box {
    @include mixins.modifierVars();

    --concentration-color: var(--color-blue);

    border: 1px solid var(--concentration-color);
    border-radius: var(--border-radius);
    padding: .5rem 1.5rem;

    @include mixins.mq(variables.$mq-phone) {
      display: none;
    }
  }

  &__headline {
    font-size: 1.25rem;
    margin-bottom: .5rem;
  }

  &__dot-container {
    display: flex;
    width: 100%;
  }

  &__dots {
    align-items: stretch;
    display: flex;
    flex-flow: row nowrap;
    justify-content: stretch;
    list-style: none;
    margin: .5rem 0 0;
    padding: 0 0;
    width: 100%;

    @include mixins.mq(variables.$mq-phone) {
      flex-flow: row wrap;

      &:not(&--show-all-announcements) {
        #{$root}__dot {
          &:nth-child(1n + 4) {
            display: none;
          }
        }
      }
    }
  }

  &__dot {
    @include mixins.modifierVars();

    --concentration-color: var(--color-blue);
    --toggle-color: var(--concentration-color);

    align-items: center;
    display: flex;
    flex: 1 1 100%;
    justify-content: center;
    padding: .5rem 0;
    position: relative;

    &:not(:last-child) {
      @include mixins.ltrtl(margin-right, margin-left, .25rem);
    }

    @include mixins.mq(variables.$mq-phone) {
      max-width: 100%;
    }

    &:hover {
      #{$root}__preview {
        opacity: 1;
        transform: translateY(0);
      }
    }
  }

  &__bar {
    background-color: var(--color-gray-400);
    border-radius: .2rem;
    cursor: pointer;
    height: .4rem;
    transition: background-color .25s;
    width: 100%;

    &--active,
    &:hover {
      background-color: var(--concentration-color);
    }

    @include mixins.mq(variables.$mq-phone) {
      display: none;
    }
  }

  &__preview {
    background-color: var(--color-white);
    border-radius: var(--border-radius);
    bottom: 1.25rem;
    box-shadow: 0 0 .25rem var(--color-gray-500);
    box-sizing: border-box;
    opacity: 0;
    padding: .5rem 1rem;
    pointer-events: none;
    position: absolute;
    transform: translateY(.5rem);
    transition: opacity .2s, transform .2s;
    width: max-content;
    z-index: 10;

    @include mixins.mq(variables.$mq-phone) {
      opacity: 1;
      pointer-events: auto;
      position: relative;
      transform: translateY(0);
      width: 100%;
    }
  }

  &__preview-content {
    cursor: pointer;
    display: flex;
    flex-flow: column;
    width: 100%;
  }

  &__text-wrapper {
    margin-bottom: 1rem;
    min-height: 14rem;
    overflow: hidden;
    transition: height .2s;
  }

  &__meta {
    align-items: center;
    color: var(--color-gray-600);
    display: flex;
    font-size: .8rem;
    font-weight: variables.$font-weight-light;
    justify-content: space-between;
    padding-top: .75rem;
  }

  &__meta-headline {
    align-items: center;
    display: flex;
    justify-content: space-between;
  }

  &__mobile-toggle {
    display: none;

    @include mixins.mq(variables.$mq-phone) {
      display: grid;
    }
  }

  &__preview-text {
    display: none;

    @include mixins.mq(variables.$mq-phone) {
      display: block;
      overflow: auto;

      p {
        display: block;
      }
    }
  }

  &__show-all-button {
    display: none;

    @include mixins.mq(variables.$mq-phone) {
      align-items: center;
      color: var(--color-blue);
      display: flex;
      justify-content: center;
      width: 100%;
    }
  }

  figure {
    margin: 0 0;
    padding: 0 0;
  }

  table {
    td {
      border-bottom: .1rem solid var(--color-gray-500);
      padding: .25rem .5rem;
      vertical-align: top;
    }
  }

  &__visible-attachment {
    margin-top: 1rem;
  }
}
</style>
